import { PortalContainerContext } from "@istari-frontend/shared";
import { Button, ResizableGrip, Tooltip, TooltipContent, TooltipTrigger } from "@istari-frontend/ui";
import {
  ArrowDownLeft,
  ArrowUpRight,
  FileText,
  KeyRound,
  MessageSquare,
  PanelBottom,
  PanelRight,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";

import { openLlmKeyPrompt, useLlmApiKey } from "~/ai-chat/data-source/llm-api-key-store";
import { AiChatCanvas, type ArtifactPreviewTarget, artifactPreviewTarget } from "~/ai-chat/feature/ai-chat-canvas";
import { AiChatComposer } from "~/ai-chat/feature/ai-chat-composer";
import { AiChatHistorySidebar } from "~/ai-chat/feature/ai-chat-history-sidebar";
import { AiChatTranscript } from "~/ai-chat/feature/ai-chat-transcript";
import { ChatSaveShareDialogs, useChatSaveShareActions } from "~/ai-chat/feature/chat-save-share-controls";
import { useAiChat } from "~/ai-chat/feature/use-ai-chat";
import { AI_CHAT_DOCK_TRANSITION_MS, useAiChatWindowSize } from "~/ai-chat/feature/use-ai-chat-window-size";
import type { ChatDockPosition } from "~/ai-chat/feature/use-chat-dock-position";
import { useChatDockPosition } from "~/ai-chat/feature/use-chat-dock-position";
import { useChatPopout } from "~/ai-chat/feature/use-chat-popout";
import { useChatPopoutMode } from "~/ai-chat/feature/use-chat-popout-mode";
import { useChatSend } from "~/ai-chat/feature/use-chat-send";
import { useChatTabs } from "~/ai-chat/feature/use-chat-tabs";
import { useConversationArtifacts } from "~/ai-chat/feature/use-conversation-artifacts";
import { useHydrateActiveConversation } from "~/ai-chat/feature/use-hydrate-active-conversation";
import type { ConversationArtifact } from "~/ai-chat/model/conversation-artifacts";
import { type ChatAction, ChatActionGroup } from "~/ai-chat/ui/chat-action-group";
import { ChatArtifactList } from "~/ai-chat/ui/chat-artifact-list";
import { ChatTabs } from "~/ai-chat/ui/chat-tabs";
import { LlmApiKeyDialog } from "~/ai-chat/ui/llm-api-key-dialog";
import { routes } from "~/routes";
import { cn } from "~/utils/cn";

export function AiChatFloatingWidget() {
  const { isOpen, close } = useAiChat();
  const location = useLocation();
  const wasOpen = useRef(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const handleClosed = useCallback(() => {
    setIsClosing(false);
  }, []);

  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      setIsClosing(true);
    }
    if (isOpen) {
      setIsClosing(false);
    }
    wasOpen.current = isOpen;
  }, [isOpen]);

  if (location.pathname === routes.AiChat) return null;
  if (!isOpen && !isClosing && !wasOpen.current) return null;

  return <AiChatWindow open={isOpen} onClose={close} onClosed={handleClosed} />;
}

function AiChatWindow({ open, onClose, onClosed }: { open: boolean; onClose: () => void; onClosed: () => void }) {
  const { dock, setDock } = useChatDockPosition();
  const { size, isResizing, resizeHandleProps } = useAiChatWindowSize(dock === "right" ? "right" : null);
  const { popoutMode } = useChatPopoutMode();
  const { popoutWindow, isPoppedOut, toggle } = useChatPopout(size, popoutMode);

  useEffect(() => {
    const root = document.documentElement;
    const clearDockCssVars = () => {
      root.style.removeProperty("--ai-chat-dock-right");
      root.style.removeProperty("--ai-chat-dock-bottom");
    };
    if (!open || isPoppedOut) {
      clearDockCssVars();
      return clearDockCssVars;
    }
    if (dock === "right") {
      root.style.setProperty("--ai-chat-dock-right", `${size.width}px`);
      root.style.removeProperty("--ai-chat-dock-bottom");
    } else {
      root.style.setProperty("--ai-chat-dock-bottom", `${size.height}px`);
      root.style.removeProperty("--ai-chat-dock-right");
    }
    return clearDockCssVars;
  }, [open, dock, size.width, size.height, isPoppedOut]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.aiChatDockTransition = "";
    const timeoutId = window.setTimeout(() => {
      delete root.dataset.aiChatDockTransition;
    }, AI_CHAT_DOCK_TRANSITION_MS);
    return () => {
      window.clearTimeout(timeoutId);
      delete root.dataset.aiChatDockTransition;
    };
  }, [open]);

  const onClosedRef = useRef(onClosed);
  useEffect(() => {
    onClosedRef.current = onClosed;
  }, [onClosed]);

  useEffect(() => {
    if (open) return undefined;
    const timeoutId = window.setTimeout(() => {
      onClosedRef.current();
    }, AI_CHAT_DOCK_TRANSITION_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  const portalContainer = isPoppedOut && popoutWindow ? popoutWindow.document.body : null;
  const panel = (
    <PortalContainerContext.Provider value={portalContainer}>
      <ChatPanel isPoppedOut={isPoppedOut} onTogglePopout={toggle} onClose={onClose} dock={dock} onSetDock={setDock} />
    </PortalContainerContext.Provider>
  );

  if (isPoppedOut && popoutWindow) {
    return createPortal(
      <div className="bg-sidebar text-foreground flex h-full flex-col">{panel}</div>,
      popoutWindow.document.body,
    );
  }

  const isRight = dock === "right";
  return (
    <aside
      aria-label="Istari Chat"
      style={
        isRight
          ? { width: size.width, top: "var(--infosec-banner-offset, 0px)" }
          : { height: size.height, left: "var(--sidebar-width, 3.5rem)" }
      }
      className={cn(
        "border-border bg-sidebar fixed z-40 flex flex-col",
        isRight
          ? cn(
              "right-0 bottom-0 max-w-[calc(100vw-2rem)] border-l-[0.5px]",
              open
                ? "animate-in slide-in-from-right-4 duration-150"
                : "animate-out slide-out-to-right-4 fade-out-0 duration-150",
            )
          : cn(
              "right-0 bottom-0 max-h-[calc(100vh-2rem)] border-t-[0.5px]",
              open
                ? "animate-in slide-in-from-bottom-4 duration-150"
                : "animate-out slide-out-to-bottom-4 fade-out-0 duration-150",
            ),
        isResizing && "transition-none select-none",
      )}
    >
      <div
        {...resizeHandleProps}
        role="separator"
        aria-label="Resize chat panel"
        aria-orientation={isRight ? "vertical" : "horizontal"}
        tabIndex={0}
        className={cn(
          "group focus-visible:ring-ring absolute z-10 flex touch-none items-center justify-center outline-none focus-visible:ring-1",
          isRight
            ? "inset-y-0 left-0 w-1.5 -translate-x-1/2 cursor-ew-resize"
            : "inset-x-0 top-0 h-1.5 -translate-y-1/2 cursor-ns-resize",
        )}
      >
        <ResizableGrip className={cn("group-focus-visible:opacity-100", !isRight && "rotate-90")} />
      </div>
      {panel}
    </aside>
  );
}

function ChatPanel({
  isPoppedOut,
  onTogglePopout,
  onClose,
  dock,
  onSetDock,
}: {
  isPoppedOut: boolean;
  onTogglePopout: () => void;
  onClose: () => void;
  dock: ChatDockPosition;
  onSetDock: (position: ChatDockPosition) => void;
}) {
  const { messages, savedArtifacts, replaceMessages, activeConversationId } = useAiChat();
  const { send, stop, retry, resume, isPending, activity } = useChatSend();
  const { hasKey, promptOpen } = useLlmApiKey();
  useHydrateActiveConversation();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [artifactsOpen, setArtifactsOpen] = useState(false);
  // The artifacts view replaces the transcript, so unseen turns must not be marked read behind it.
  const chatTabs = useChatTabs({ conversationVisible: !historyOpen && !artifactsOpen });
  const artifacts = useConversationArtifacts(messages, savedArtifacts);
  const [artifactPreview, setArtifactPreview] = useState<ArtifactPreviewTarget | null>(null);
  // Switching conversations must drop the previewed draft — it belongs to the
  // conversation we just left, not the one now on screen.
  useEffect(() => {
    setArtifactPreview(null);
  }, [activeConversationId]);
  const openFromArtifactList = (artifact: ConversationArtifact) => setArtifactPreview(artifactPreviewTarget(artifact));
  const { actions: saveShareActions, saveShare } = useChatSaveShareActions();
  const actions: ChatAction[] = [
    {
      key: "artifacts",
      icon: artifactsOpen ? MessageSquare : FileText,
      label: artifactsOpen ? "Chat" : "Artifacts",
      pressed: artifactsOpen,
      onClick: () => {
        setArtifactsOpen((prev) => !prev);
        setArtifactPreview(null);
      },
    },
    ...saveShareActions,
    {
      key: "llm-key",
      icon: KeyRound,
      label: hasKey ? "Update your LLM key" : "Set your LLM key",
      pressed: promptOpen,
      onClick: openLlmKeyPrompt,
    },
  ];

  const handleEdit = (messageId: string, content: string) => {
    const index = messages.findIndex((item) => item.id === messageId);
    if (index === -1) return;
    const truncated = messages.slice(0, index);
    replaceMessages(truncated);
    // Pass the truncated history explicitly: `send`'s own `messages` closure is
    // still the pre-edit list this tick, so the discarded turns would otherwise
    // be re-sent to the backend.
    void send(content, truncated);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header
        className="border-border flex h-14 shrink-0 items-center justify-between gap-2 border-b-[0.5px] pr-3.5 pl-5"
        data-widget-header
      >
        <span className="text-secondary-foreground min-w-0 truncate text-lg font-semibold">Istari AI Chat</span>
        <div className="flex items-center gap-0.5">
          {!isPoppedOut && (
            <div className="border-input mr-0.5 flex items-center overflow-hidden rounded-md border">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    className={cn("rounded-none px-1.5", dock === "right" && "bg-muted text-foreground")}
                    onClick={() => onSetDock("right")}
                    aria-label="Dock chat to the right"
                    aria-pressed={dock === "right"}
                  >
                    <PanelRight className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Dock right</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="xs"
                    className={cn("rounded-none px-1.5", dock === "bottom" && "bg-muted text-foreground")}
                    onClick={() => onSetDock("bottom")}
                    aria-label="Dock chat to the bottom"
                    aria-pressed={dock === "bottom"}
                  >
                    <PanelBottom className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Dock bottom</TooltipContent>
              </Tooltip>
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="xs"
                className="px-1.5"
                onClick={onTogglePopout}
                aria-label={isPoppedOut ? "Dock chat back into the app" : "Open chat in a separate window"}
              >
                {isPoppedOut ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isPoppedOut ? "Dock back into the app" : "Open in a separate window"}
            </TooltipContent>
          </Tooltip>
          {!isPoppedOut && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="xs" className="px-1.5" onClick={onClose} aria-label="Close Istari AI">
                  <XIcon className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close</TooltipContent>
            </Tooltip>
          )}
        </div>
      </header>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {historyOpen ? (
          <div className="min-h-0 min-w-0 flex-1">
            <AiChatHistorySidebar onAfterSelect={() => setHistoryOpen(false)} />
          </div>
        ) : (
          <>
            <ChatTabs
              tabs={chatTabs.tabs}
              onSelect={chatTabs.switchTo}
              onClose={chatTabs.closeTab}
              onNew={chatTabs.startNew}
              onRename={chatTabs.renameTab}
              onOpenHistory={() => setHistoryOpen(true)}
            />
            <div className="relative flex min-h-0 flex-1 flex-col p-2">
              <ChatActionGroup actions={actions} className="absolute top-1 right-1 z-10" />
              <div className="bg-background flex min-h-0 flex-1 flex-col rounded-sm">
                {artifactsOpen ? (
                  artifactPreview ? (
                    <AiChatCanvas
                      file={artifactPreview}
                      wide
                      className="bg-background border-l-0"
                      onBack={() => setArtifactPreview(null)}
                      onClose={() => {
                        setArtifactPreview(null);
                        setArtifactsOpen(false);
                      }}
                    />
                  ) : (
                    <div className="flex min-h-0 flex-1 flex-col">
                      <h2 className="text-foreground px-4 pt-5 pb-2 text-lg leading-7 font-medium">Artifacts</h2>
                      <ChatArtifactList artifacts={artifacts} onOpen={openFromArtifactList} />
                    </div>
                  )
                ) : (
                  <>
                    <AiChatTranscript
                      isPending={isPending}
                      activity={activity}
                      onSuggestionSelect={(suggestion) => void send(suggestion)}
                      onRetry={retry}
                      onEdit={handleEdit}
                      onApproveResume={resume}
                    />
                    <AiChatComposer
                      key={chatTabs.activeKey}
                      onSend={(content) => void send(content)}
                      onStop={stop}
                      isPending={isPending}
                      className="border-t-0"
                    />
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <LlmApiKeyDialog />
      <ChatSaveShareDialogs saveShare={saveShare} />
    </div>
  );
}
